import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  FileCode2, Copy, Download, Settings2, Shield, Layers, 
  CheckCircle2, Info, Lightbulb, Cog, BookTemplate,
  Building2, Heart, Landmark, Server, Globe, Factory, MapPin,
  Upload, FileUp, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Framework definitions organized by category
const FRAMEWORK_CATEGORIES = {
  'ISO 27000 Series': {
    icon: Shield,
    frameworks: [
      { id: 'iso27001', name: 'ISO 27001:2022', description: 'Information Security Management' },
      { id: 'iso27002', name: 'ISO 27002:2022', description: 'Security Controls' },
      { id: 'iso27017', name: 'ISO 27017', description: 'Cloud Security' },
      { id: 'iso27018', name: 'ISO 27018', description: 'Cloud Privacy' },
      { id: 'iso27701', name: 'ISO 27701', description: 'Privacy Information Management' },
    ]
  },
  'AI & Technology': {
    icon: Server,
    frameworks: [
      { id: 'iso42001', name: 'ISO 42001', description: 'AI Management System' },
      { id: 'nistAI', name: 'NIST AI RMF', description: 'AI Risk Management Framework' },
      { id: 'euAI', name: 'EU AI Act', description: 'European AI Regulation' },
    ]
  },
  'Privacy Regulations': {
    icon: Globe,
    frameworks: [
      { id: 'gdpr', name: 'GDPR', description: 'EU General Data Protection' },
      { id: 'ccpa', name: 'CCPA', description: 'California Consumer Privacy' },
      { id: 'dpdpa', name: 'DPDPA (India)', description: 'Digital Personal Data Protection' },
      { id: 'lgpd', name: 'LGPD (Brazil)', description: 'Lei Geral de Proteção de Dados' },
      { id: 'popia', name: 'POPIA', description: 'South Africa Protection of Personal Information' },
    ]
  },
  'Healthcare': {
    icon: Heart,
    frameworks: [
      { id: 'hipaa', name: 'HIPAA', description: 'Health Insurance Portability' },
      { id: 'hiTrust', name: 'HITRUST', description: 'Health Information Trust Alliance' },
      { id: 'hitech', name: 'HITECH', description: 'Health IT for Economic & Clinical Health' },
      { id: 'nhsDsp', name: 'NHS DSP', description: 'NHS Data Security & Protection' },
    ]
  },
  'Financial Services': {
    icon: Landmark,
    frameworks: [
      { id: 'pciDss', name: 'PCI DSS', description: 'Payment Card Industry Data Security' },
      { id: 'sox', name: 'SOX', description: 'Sarbanes-Oxley Act' },
      { id: 'glba', name: 'GLBA', description: 'Gramm-Leach-Bliley Act' },
      { id: 'ffiec', name: 'FFIEC', description: 'Federal Financial Institutions' },
      { id: 'mas', name: 'MAS TRM', description: 'Monetary Authority of Singapore' },
    ]
  },
  'SOC Reports': {
    icon: CheckCircle2,
    frameworks: [
      { id: 'soc1', name: 'SOC 1', description: 'Internal Controls over Financial Reporting' },
      { id: 'soc2Type1', name: 'SOC 2 Type I', description: 'Trust Services - Point in Time' },
      { id: 'soc2Type2', name: 'SOC 2 Type II', description: 'Trust Services - Period of Time' },
      { id: 'soc3', name: 'SOC 3', description: 'Trust Services - Public Report' },
    ]
  },
  'Government & Defense': {
    icon: Building2,
    frameworks: [
      { id: 'nist80053', name: 'NIST 800-53', description: 'Security and Privacy Controls' },
      { id: 'nistCsf', name: 'NIST CSF', description: 'Cybersecurity Framework' },
      { id: 'fedramp', name: 'FedRAMP', description: 'Federal Risk Authorization' },
      { id: 'cmmc', name: 'CMMC', description: 'Cybersecurity Maturity Model' },
      { id: 'cis', name: 'CIS Controls', description: 'Center for Internet Security' },
    ]
  },
  'Industry Specific': {
    icon: Factory,
    frameworks: [
      { id: 'nercCip', name: 'NERC CIP', description: 'Critical Infrastructure Protection' },
      { id: 'iec62443', name: 'IEC 62443', description: 'Industrial Automation Security' },
      { id: 'tisax', name: 'TISAX', description: 'Automotive Information Security' },
      { id: 'csaStar', name: 'CSA STAR', description: 'Cloud Security Alliance' },
    ]
  },
  'Regional Standards': {
    icon: MapPin,
    frameworks: [
      { id: 'sama', name: 'SAMA (Saudi)', description: 'Saudi Arabian Monetary Authority' },
      { id: 'nesa', name: 'NESA (UAE)', description: 'National Electronic Security Authority' },
      { id: 'pdpl', name: 'PDPL (Qatar)', description: 'Personal Data Protection Law' },
      { id: 'cbk', name: 'CBK (Kuwait)', description: 'Central Bank of Kuwait' },
    ]
  },
};

// Control domain definitions
const CONTROL_DOMAINS = [
  { id: 'access', label: 'Access Control', categoryId: 'AC' },
  { id: 'incident', label: 'Incident Response', categoryId: 'IR' },
  { id: 'risk', label: 'Risk Management', categoryId: 'RM' },
  { id: 'crypto', label: 'Cryptography', categoryId: 'CRY' },
  { id: 'asset', label: 'Asset Management', categoryId: 'AM' },
  { id: 'vendor', label: 'Vendor Management', categoryId: 'VM' },
  { id: 'continuity', label: 'Business Continuity', categoryId: 'BC' },
  { id: 'training', label: 'Training & Awareness', categoryId: 'TRN' },
  { id: 'audit', label: 'Audit & Monitoring', categoryId: 'AUD' },
  { id: 'privacy', label: 'Privacy & DLP', categoryId: 'PRI' },
  { id: 'threat', label: 'Threat Intelligence', categoryId: 'THR' },
  { id: 'vendorRisk', label: 'Vendor Risk', categoryId: 'VR' },
];

// Pre-built templates
const TEMPLATES = {
  'Full Enterprise': {
    description: 'Comprehensive coverage for large enterprises with global operations',
    frameworks: ['iso27001', 'iso27002', 'iso27017', 'iso27018', 'iso27701', 'nistCsf', 'gdpr', 'hipaa', 'pciDss', 'soc2Type2'],
    strategy: 'hybrid',
    domains: ['access', 'incident', 'risk', 'crypto', 'asset', 'vendor', 'continuity', 'training', 'audit', 'privacy', 'threat', 'vendorRisk'],
    automationLevel: 'fully-automated',
    riskModel: 'cvss'
  },
  'Healthcare Provider': {
    description: 'Focused on healthcare compliance including HIPAA and HITRUST',
    frameworks: ['hipaa', 'hiTrust', 'hitech', 'iso27001', 'gdpr', 'nistCsf', 'soc2Type2'],
    strategy: 'unified',
    domains: ['access', 'incident', 'risk', 'asset', 'continuity', 'training', 'audit', 'privacy'],
    automationLevel: 'semi-automated',
    riskModel: 'business-impact'
  },
  'Financial Services': {
    description: 'Banking and financial services compliance requirements',
    frameworks: ['pciDss', 'sox', 'glba', 'ffiec', 'iso27001', 'nistCsf', 'gdpr', 'soc1', 'soc2Type2'],
    strategy: 'unified',
    domains: ['access', 'incident', 'risk', 'crypto', 'asset', 'vendor', 'continuity', 'audit', 'privacy'],
    automationLevel: 'fully-automated',
    riskModel: 'cvss'
  },
  'Cloud Service Provider': {
    description: 'For SaaS/PaaS/IaaS providers with multi-tenant environments',
    frameworks: ['iso27001', 'iso27017', 'iso27018', 'csaStar', 'soc2Type2', 'gdpr', 'ccpa', 'fedramp'],
    strategy: 'cross-referenced',
    domains: ['access', 'incident', 'risk', 'crypto', 'asset', 'continuity', 'audit', 'privacy', 'threat'],
    automationLevel: 'fully-automated',
    riskModel: 'cvss'
  },
  'Government Agency': {
    description: 'Federal and state government compliance requirements',
    frameworks: ['nist80053', 'nistCsf', 'fedramp', 'cmmc', 'cis'],
    strategy: 'unified',
    domains: ['access', 'incident', 'risk', 'crypto', 'asset', 'vendor', 'continuity', 'training', 'audit', 'threat'],
    automationLevel: 'semi-automated',
    riskModel: 'cvss'
  },
  'Critical Infrastructure': {
    description: 'Energy, utilities, and critical infrastructure protection',
    frameworks: ['nercCip', 'iec62443', 'nist80053', 'nistCsf', 'cis', 'iso27001'],
    strategy: 'hybrid',
    domains: ['access', 'incident', 'risk', 'asset', 'continuity', 'audit', 'threat'],
    automationLevel: 'semi-automated',
    riskModel: 'business-impact'
  },
  'Privacy-First Organization': {
    description: 'Organizations prioritizing data privacy and protection',
    frameworks: ['gdpr', 'ccpa', 'lgpd', 'popia', 'dpdpa', 'iso27701', 'iso27018'],
    strategy: 'unified',
    domains: ['access', 'risk', 'asset', 'training', 'audit', 'privacy'],
    automationLevel: 'semi-automated',
    riskModel: 'business-impact'
  },
  'AI/Tech Company': {
    description: 'Technology companies developing AI and ML solutions',
    frameworks: ['iso42001', 'nistAI', 'euAI', 'iso27001', 'gdpr', 'ccpa', 'soc2Type2'],
    strategy: 'hybrid',
    domains: ['access', 'risk', 'asset', 'vendor', 'training', 'audit', 'privacy', 'threat'],
    automationLevel: 'fully-automated',
    riskModel: 'cvss'
  },
};

// Framework name mapping for YAML output
const getFrameworkDisplayName = (id) => {
  for (const category of Object.values(FRAMEWORK_CATEGORIES)) {
    const framework = category.frameworks.find(f => f.id === id);
    if (framework) return framework.name;
  }
  return id;
};

// Get framework ID from display name
const getFrameworkIdFromName = (name) => {
  for (const category of Object.values(FRAMEWORK_CATEGORIES)) {
    const framework = category.frameworks.find(f => f.name === name || f.name.toLowerCase() === name.toLowerCase());
    if (framework) return framework.id;
  }
  return null;
};

const YAMLBuilder = () => {
  const [activeTab, setActiveTab] = useState('basic');
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [yamlOutput, setYamlOutput] = useState('');
  const [importError, setImportError] = useState('');
  const fileInputRef = useRef(null);

  // Basic config state
  const [config, setConfig] = useState({
    platformName: 'Enterprise GRC Platform',
    version: '1.0.0',
    organization: '',
    description: 'Multi-standard compliance framework supporting ISO 27001, NIST, GDPR, HIPAA, PCI-DSS, and regional standards.',
  });

  // Frameworks state
  const [selectedFrameworks, setSelectedFrameworks] = useState([
    'iso27001', 'iso27002', 'iso27017', 'iso27018', 'iso27701',
    'iso42001', 'nistAI', 'euAI',
    'gdpr', 'ccpa', 'dpdpa', 'lgpd', 'popia',
    'hipaa', 'hiTrust', 'hitech', 'nhsDsp',
    'pciDss', 'sox', 'glba', 'ffiec', 'mas',
    'soc1', 'soc2Type1', 'soc2Type2', 'soc3',
    'nist80053', 'nistCsf', 'fedramp', 'cmmc', 'cis',
    'nercCip', 'iec62443', 'tisax', 'csaStar',
    'sama', 'nesa', 'pdpl', 'cbk'
  ]);

  // Controls config state
  const [controlsConfig, setControlsConfig] = useState({
    mappingStrategy: 'unified',
    selectedDomains: ['access', 'incident', 'risk', 'crypto', 'asset', 'vendor', 'continuity', 'training', 'audit', 'privacy', 'threat', 'vendorRisk'],
  });

  // Advanced config state
  const [advancedConfig, setAdvancedConfig] = useState({
    reportingPeriod: 12,
    assessmentInterval: 30,
    automationLevel: 'manual',
    riskModel: 'cvss',
    storagePath: '/evidence',
  });

  // Generate YAML output
  const generateYAML = useCallback(() => {
    const now = new Date().toISOString();
    const selectedFrameworkNames = selectedFrameworks
      .map(id => getFrameworkDisplayName(id))
      .sort();

    const domainConfigs = controlsConfig.selectedDomains.map(domainId => {
      const domain = CONTROL_DOMAINS.find(d => d.id === domainId);
      return {
        id: domain?.categoryId || domainId.toUpperCase(),
        name: domain?.label || domainId,
        enabled: true,
      };
    });

    // Control categories with framework mappings
    const controlCategories = [
      { category: 'Access Control', categoryId: 'AC', controlsCount: 12 },
      { category: 'Incident Management', categoryId: 'IM', controlsCount: 8 },
      { category: 'Cryptography', categoryId: 'CRY', controlsCount: 6 },
      { category: 'Risk Management', categoryId: 'RM', controlsCount: 10 },
      { category: 'Asset Management', categoryId: 'AM', controlsCount: 7 },
      { category: 'Business Continuity', categoryId: 'BC', controlsCount: 6 },
      { category: 'Vendor Management', categoryId: 'VM', controlsCount: 8 },
      { category: 'Audit & Monitoring', categoryId: 'AUD', controlsCount: 9 },
      { category: 'Training & Awareness', categoryId: 'TRN', controlsCount: 5 },
      { category: 'Privacy & Data Protection', categoryId: 'PRI', controlsCount: 11 },
      { category: 'Threat Intelligence', categoryId: 'THR', controlsCount: 7 },
    ];

    const yaml = `# GRC Platform Configuration
# Generated: ${now}
# Platform: ${config.platformName} v${config.version}

grc:
  metadata:
    name: "${config.platformName}"
    version: "${config.version}"
    organization: "${config.organization}"
    description: "${config.description}"
    generated_date: "${now}"

  compliance_frameworks:
    enabled: ${selectedFrameworks.length}
    standards:
${selectedFrameworkNames.map(name => `      - name: "${name}"`).join('\n')}

  controls:
    mapping_strategy: "${controlsConfig.mappingStrategy}"
    
    assessment_config:
      interval_days: ${advancedConfig.assessmentInterval}
      reporting_period_months: ${advancedConfig.reportingPeriod}
      automation_level: "${advancedConfig.automationLevel}"
      risk_scoring_model: "${advancedConfig.riskModel}"
    
    domains:
${domainConfigs.map(d => `      - id: "${d.id}"
        name: "${d.name}"
        enabled: ${d.enabled}
        controls_enabled: true`).join('\n')}
    
    evidence:
      storage_path: "${advancedConfig.storagePath}"
      retention_days: 2555
      encryption_enabled: true
      versioning_enabled: true

  unified_control_framework:
    enabled: true
    description: "Single source of truth for all compliance controls"
    mapping_strategy: "cross-framework-alignment"
    
    control_categories:
${controlCategories.map(cat => `      - category: "${cat.category}"
        category_id: "${cat.categoryId}"
        controls_count: ${cat.controlsCount}
        frameworks:
${selectedFrameworkNames.slice(0, 3).map(f => `          - "${f}"`).join('\n')}`).join('\n\n')}

  automation:
    enabled: true
    integrations:
      - type: "cicd_security"
        name: "Pipeline Security Gates"
        enabled: true
      
      - type: "vulnerability_scanning"
        name: "Automated Vulnerability Assessment"
        enabled: true
      
      - type: "configuration_management"
        name: "Infrastructure as Code Compliance"
        enabled: true
      
      - type: "evidence_collection"
        name: "Automated Evidence Gathering"
        enabled: true
      
      - type: "reporting"
        name: "Compliance Reporting"
        enabled: true

  reporting:
    enabled: true
    formats:
      - format: "json"
        enabled: true
      - format: "xml"
        enabled: true
      - format: "pdf"
        enabled: true
      - format: "xlsx"
        enabled: true
    
    dashboards:
      - name: "Compliance Status Overview"
        metrics:
          - "control_coverage"
          - "control_implementation_status"
          - "risk_score"
      
      - name: "Framework Alignment"
        metrics:
          - "framework_coverage_percentage"
          - "control_mapping_completeness"
      
      - name: "Control Assessment Results"
        metrics:
          - "control_effectiveness"
          - "audit_findings_by_domain"

  audit_trail:
    enabled: true
    logging_level: "comprehensive"
    retention_period_days: 2555
    immutable: true

  roles_and_permissions:
    enabled: true
    default_roles:
      - role: "grc_admin"
        permissions: ["read", "write", "delete", "configure"]
      
      - role: "compliance_manager"
        permissions: ["read", "write", "approve", "report"]
      
      - role: "control_owner"
        permissions: ["read", "write", "evidence_upload"]
      
      - role: "auditor"
        permissions: ["read", "report"]

  api:
    enabled: true
    version: "v1"
    authentication: "oauth2"
    rate_limiting: 1000
    endpoints:
      - /compliance/frameworks
      - /controls
      - /assessments
      - /evidence
      - /reports
      - /audit-trail`;

    setYamlOutput(yaml);
  }, [config, selectedFrameworks, controlsConfig, advancedConfig]);

  // Generate YAML on any config change
  useEffect(() => {
    generateYAML();
  }, [generateYAML]);

  // Handle framework toggle
  const toggleFramework = (frameworkId) => {
    setSelectedFrameworks(prev => 
      prev.includes(frameworkId)
        ? prev.filter(id => id !== frameworkId)
        : [...prev, frameworkId]
    );
  };

  // Handle domain toggle
  const toggleDomain = (domainId) => {
    setControlsConfig(prev => ({
      ...prev,
      selectedDomains: prev.selectedDomains.includes(domainId)
        ? prev.selectedDomains.filter(id => id !== domainId)
        : [...prev.selectedDomains, domainId]
    }));
  };

  // Select all frameworks in a category
  const selectAllInCategory = (categoryKey, select) => {
    const category = FRAMEWORK_CATEGORIES[categoryKey];
    const frameworkIds = category.frameworks.map(f => f.id);
    
    setSelectedFrameworks(prev => {
      if (select) {
        return [...new Set([...prev, ...frameworkIds])];
      } else {
        return prev.filter(id => !frameworkIds.includes(id));
      }
    });
  };

  // Load template
  const loadTemplate = (templateName) => {
    const template = TEMPLATES[templateName];
    if (!template) return;

    setSelectedFrameworks(template.frameworks);
    setControlsConfig({
      mappingStrategy: template.strategy,
      selectedDomains: template.domains,
    });
    setAdvancedConfig(prev => ({
      ...prev,
      automationLevel: template.automationLevel,
      riskModel: template.riskModel,
    }));
    
    setShowTemplateDialog(false);
    toast.success(`Loaded "${templateName}" template`);
  };

  // Parse YAML and import configuration
  const parseYAMLConfig = (yamlContent) => {
    try {
      setImportError('');
      
      // Simple YAML parser for our specific format
      const lines = yamlContent.split('\n');
      let newConfig = { ...config };
      let newFrameworks = [];
      let newStrategy = controlsConfig.mappingStrategy;
      let newDomains = [];
      let newAdvanced = { ...advancedConfig };
      
      let currentSection = '';
      let inStandards = false;
      let inDomains = false;
      
      for (const line of lines) {
        const trimmed = line.trim();
        
        // Skip comments and empty lines
        if (trimmed.startsWith('#') || trimmed === '') continue;
        
        // Detect sections
        if (trimmed === 'metadata:') currentSection = 'metadata';
        else if (trimmed === 'compliance_frameworks:') currentSection = 'frameworks';
        else if (trimmed === 'controls:') currentSection = 'controls';
        else if (trimmed === 'assessment_config:') currentSection = 'assessment';
        else if (trimmed === 'standards:') { inStandards = true; continue; }
        else if (trimmed === 'domains:') { inDomains = true; continue; }
        
        // Parse metadata
        if (currentSection === 'metadata') {
          if (trimmed.startsWith('name:')) {
            newConfig.platformName = trimmed.replace('name:', '').trim().replace(/"/g, '');
          } else if (trimmed.startsWith('version:')) {
            newConfig.version = trimmed.replace('version:', '').trim().replace(/"/g, '');
          } else if (trimmed.startsWith('organization:')) {
            newConfig.organization = trimmed.replace('organization:', '').trim().replace(/"/g, '');
          } else if (trimmed.startsWith('description:')) {
            newConfig.description = trimmed.replace('description:', '').trim().replace(/"/g, '');
          }
        }
        
        // Parse frameworks
        if (inStandards && trimmed.startsWith('- name:')) {
          const frameworkName = trimmed.replace('- name:', '').trim().replace(/"/g, '');
          const frameworkId = getFrameworkIdFromName(frameworkName);
          if (frameworkId) {
            newFrameworks.push(frameworkId);
          }
        }
        
        // Stop parsing standards when we hit a new section
        if (inStandards && !trimmed.startsWith('-') && !trimmed.startsWith('- name:') && trimmed.includes(':')) {
          inStandards = false;
        }
        
        // Parse controls
        if (currentSection === 'controls') {
          if (trimmed.startsWith('mapping_strategy:')) {
            newStrategy = trimmed.replace('mapping_strategy:', '').trim().replace(/"/g, '');
          }
        }
        
        // Parse assessment config
        if (currentSection === 'assessment') {
          if (trimmed.startsWith('interval_days:')) {
            newAdvanced.assessmentInterval = parseInt(trimmed.replace('interval_days:', '').trim()) || 30;
          } else if (trimmed.startsWith('reporting_period_months:')) {
            newAdvanced.reportingPeriod = parseInt(trimmed.replace('reporting_period_months:', '').trim()) || 12;
          } else if (trimmed.startsWith('automation_level:')) {
            newAdvanced.automationLevel = trimmed.replace('automation_level:', '').trim().replace(/"/g, '');
          } else if (trimmed.startsWith('risk_scoring_model:')) {
            newAdvanced.riskModel = trimmed.replace('risk_scoring_model:', '').trim().replace(/"/g, '');
          }
        }
        
        // Parse domains
        if (inDomains && trimmed.startsWith('- id:')) {
          const domainId = trimmed.replace('- id:', '').trim().replace(/"/g, '');
          // Map domain IDs back to our internal IDs
          const domainMapping = {
            'AC': 'access', 'IR': 'incident', 'RM': 'risk', 'CRY': 'crypto',
            'AM': 'asset', 'VM': 'vendor', 'BC': 'continuity', 'TRN': 'training',
            'AUD': 'audit', 'PRI': 'privacy', 'THR': 'threat', 'VR': 'vendorRisk'
          };
          const internalId = domainMapping[domainId] || domainId.toLowerCase();
          if (CONTROL_DOMAINS.find(d => d.id === internalId)) {
            newDomains.push(internalId);
          }
        }
        
        // Stop parsing domains when we hit evidence section
        if (inDomains && trimmed === 'evidence:') {
          inDomains = false;
        }
      }
      
      // Apply parsed configuration
      setConfig(newConfig);
      if (newFrameworks.length > 0) {
        setSelectedFrameworks(newFrameworks);
      }
      setControlsConfig(prev => ({
        ...prev,
        mappingStrategy: newStrategy,
        selectedDomains: newDomains.length > 0 ? newDomains : prev.selectedDomains,
      }));
      setAdvancedConfig(newAdvanced);
      
      setShowImportDialog(false);
      toast.success(`Imported configuration: ${newConfig.platformName}`);
      
    } catch (error) {
      console.error('YAML parse error:', error);
      setImportError('Failed to parse YAML file. Please check the format.');
    }
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.yaml') && !file.name.endsWith('.yml')) {
      setImportError('Please select a YAML file (.yaml or .yml)');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result;
      if (typeof content === 'string') {
        parseYAMLConfig(content);
      }
    };
    reader.onerror = () => {
      setImportError('Failed to read file');
    };
    reader.readAsText(file);
  };

  // Handle paste import
  const [pasteContent, setPasteContent] = useState('');
  
  const handlePasteImport = () => {
    if (!pasteContent.trim()) {
      setImportError('Please paste YAML content');
      return;
    }
    parseYAMLConfig(pasteContent);
    setPasteContent('');
  };

  // Copy to clipboard
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(yamlOutput);
      toast.success('YAML copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  // Download YAML file
  const downloadYAML = () => {
    const filename = `grc-config-${config.platformName.toLowerCase().replace(/\s+/g, '-')}-${config.version}.yaml`;
    const blob = new Blob([yamlOutput], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  };

  return (
    <div className="p-6 space-y-6" data-testid="yaml-builder">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileCode2 className="w-7 h-7 text-blue-600" />
            GRC Platform YAML Builder
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Configure and generate YAML configurations for your GRC platform
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => setShowImportDialog(true)}
            data-testid="import-yaml-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import YAML
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowTemplateDialog(true)}
            data-testid="load-template-btn"
          >
            <BookTemplate className="w-4 h-4 mr-2" />
            Load Template
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Configuration */}
        <Card className="lg:row-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Configuration Builder
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-4 mb-4">
                <TabsTrigger value="basic" data-testid="tab-basic">Basic</TabsTrigger>
                <TabsTrigger value="frameworks" data-testid="tab-frameworks">Frameworks</TabsTrigger>
                <TabsTrigger value="controls" data-testid="tab-controls">Controls</TabsTrigger>
                <TabsTrigger value="advanced" data-testid="tab-advanced">Advanced</TabsTrigger>
              </TabsList>

              {/* Basic Tab */}
              <TabsContent value="basic" className="space-y-4">
                <div className="space-y-2">
                  <Label>Platform Name</Label>
                  <Input
                    placeholder="e.g., Enterprise GRC Platform"
                    value={config.platformName}
                    onChange={(e) => setConfig({ ...config, platformName: e.target.value })}
                    data-testid="input-platform-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Version</Label>
                  <Input
                    placeholder="e.g., 1.0.0"
                    value={config.version}
                    onChange={(e) => setConfig({ ...config, version: e.target.value })}
                    data-testid="input-version"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Organization</Label>
                  <Input
                    placeholder="Your organization name"
                    value={config.organization}
                    onChange={(e) => setConfig({ ...config, organization: e.target.value })}
                    data-testid="input-organization"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    placeholder="Brief description of your GRC platform..."
                    value={config.description}
                    onChange={(e) => setConfig({ ...config, description: e.target.value })}
                    rows={3}
                    data-testid="input-description"
                  />
                </div>

                <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mt-4">
                  <div className="flex gap-2">
                    <Lightbulb className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <strong>Tip:</strong> Configure basic metadata about your GRC platform. This appears in all generated YAML outputs.
                    </p>
                  </div>
                </div>
              </TabsContent>

              {/* Frameworks Tab */}
              <TabsContent value="frameworks" className="space-y-4">
                <ScrollArea className="h-[500px] pr-4">
                  {Object.entries(FRAMEWORK_CATEGORIES).map(([categoryName, category]) => {
                    const CategoryIcon = category.icon;
                    const allSelected = category.frameworks.every(f => selectedFrameworks.includes(f.id));
                    const someSelected = category.frameworks.some(f => selectedFrameworks.includes(f.id));
                    
                    return (
                      <div key={categoryName} className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold flex items-center gap-2">
                            <CategoryIcon className="w-4 h-4 text-muted-foreground" />
                            {categoryName}
                          </h3>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-7"
                            onClick={() => selectAllInCategory(categoryName, !allSelected)}
                          >
                            {allSelected ? 'Deselect All' : 'Select All'}
                          </Button>
                        </div>
                        <div className="grid grid-cols-1 gap-2 pl-6">
                          {category.frameworks.map((framework) => (
                            <div
                              key={framework.id}
                              className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50"
                            >
                              <Checkbox
                                id={framework.id}
                                checked={selectedFrameworks.includes(framework.id)}
                                onCheckedChange={() => toggleFramework(framework.id)}
                                data-testid={`checkbox-${framework.id}`}
                              />
                              <label
                                htmlFor={framework.id}
                                className="flex-1 cursor-pointer"
                              >
                                <span className="font-medium text-sm">{framework.name}</span>
                                <span className="text-xs text-muted-foreground ml-2">
                                  - {framework.description}
                                </span>
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </ScrollArea>
                
                <div className="flex items-center justify-between pt-2 border-t">
                  <Badge variant="secondary">
                    {selectedFrameworks.length} frameworks selected
                  </Badge>
                </div>
              </TabsContent>

              {/* Controls Tab */}
              <TabsContent value="controls" className="space-y-4">
                <div className="space-y-2">
                  <Label>Control Mapping Strategy</Label>
                  <Select
                    value={controlsConfig.mappingStrategy}
                    onValueChange={(v) => setControlsConfig({ ...controlsConfig, mappingStrategy: v })}
                  >
                    <SelectTrigger data-testid="select-strategy">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unified">Unified Control Framework (Single source of truth)</SelectItem>
                      <SelectItem value="cross-referenced">Cross-Referenced (Framework-specific mappings)</SelectItem>
                      <SelectItem value="hybrid">Hybrid (Unified + framework-specific)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Domain Categories</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {CONTROL_DOMAINS.map((domain) => (
                      <div
                        key={domain.id}
                        className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50"
                      >
                        <Checkbox
                          id={`domain-${domain.id}`}
                          checked={controlsConfig.selectedDomains.includes(domain.id)}
                          onCheckedChange={() => toggleDomain(domain.id)}
                          data-testid={`checkbox-domain-${domain.id}`}
                        />
                        <label
                          htmlFor={`domain-${domain.id}`}
                          className="text-sm cursor-pointer"
                        >
                          {domain.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-lg p-3 mt-4">
                  <div className="flex gap-2">
                    <Info className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-purple-700 dark:text-purple-300">
                      <strong>Note:</strong> Select control domains that apply to your organization's compliance needs.
                    </p>
                  </div>
                </div>
              </TabsContent>

              {/* Advanced Tab */}
              <TabsContent value="advanced" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Reporting Period (Months)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={36}
                      value={advancedConfig.reportingPeriod}
                      onChange={(e) => setAdvancedConfig({ ...advancedConfig, reportingPeriod: parseInt(e.target.value) || 12 })}
                      data-testid="input-reporting-period"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Assessment Interval (Days)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={advancedConfig.assessmentInterval}
                      onChange={(e) => setAdvancedConfig({ ...advancedConfig, assessmentInterval: parseInt(e.target.value) || 30 })}
                      data-testid="input-assessment-interval"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Automation Level</Label>
                  <Select
                    value={advancedConfig.automationLevel}
                    onValueChange={(v) => setAdvancedConfig({ ...advancedConfig, automationLevel: v })}
                  >
                    <SelectTrigger data-testid="select-automation">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="semi-automated">Semi-Automated</SelectItem>
                      <SelectItem value="fully-automated">Fully Automated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Risk Scoring Model</Label>
                  <Select
                    value={advancedConfig.riskModel}
                    onValueChange={(v) => setAdvancedConfig({ ...advancedConfig, riskModel: v })}
                  >
                    <SelectTrigger data-testid="select-risk-model">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cvss">CVSS 3.1</SelectItem>
                      <SelectItem value="custom">Custom Scoring</SelectItem>
                      <SelectItem value="business-impact">Business Impact</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Evidence Storage Path</Label>
                  <Input
                    value={advancedConfig.storagePath}
                    onChange={(e) => setAdvancedConfig({ ...advancedConfig, storagePath: e.target.value })}
                    data-testid="input-storage-path"
                  />
                </div>

                <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mt-4">
                  <div className="flex gap-2">
                    <Cog className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>Advanced Settings:</strong> Configure automation, assessment intervals, and evidence management options.
                    </p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Right Panel - YAML Output */}
        <Card className="lg:row-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileCode2 className="w-5 h-5" />
                Generated YAML Output
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={copyToClipboard} data-testid="btn-copy">
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </Button>
                <Button variant="outline" size="sm" onClick={downloadYAML} data-testid="btn-download">
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
              </div>
            </div>
            <CardDescription>
              Real-time preview of your GRC configuration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px]">
              <pre className="text-xs font-mono bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap">
                {yamlOutput}
              </pre>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookTemplate className="w-5 h-5" />
              Load Pre-built Template
            </DialogTitle>
            <DialogDescription>
              Choose a template to quickly configure your GRC platform for common scenarios
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            {Object.entries(TEMPLATES).map(([name, template]) => (
              <Card
                key={name}
                className="cursor-pointer hover:shadow-lg transition-all hover:border-primary"
                onClick={() => loadTemplate(name)}
                data-testid={`template-${name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{name}</CardTitle>
                  <CardDescription className="text-xs">
                    {template.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Frameworks</span>
                      <Badge variant="secondary">{template.frameworks.length}</Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Domains</span>
                      <Badge variant="secondary">{template.domains.length}</Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Strategy</span>
                      <Badge variant="outline" className="capitalize">{template.strategy}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {template.frameworks.slice(0, 4).map(fwId => (
                        <Badge key={fwId} variant="secondary" className="text-xs">
                          {getFrameworkDisplayName(fwId).split(' ')[0]}
                        </Badge>
                      ))}
                      {template.frameworks.length > 4 && (
                        <Badge variant="secondary" className="text-xs">
                          +{template.frameworks.length - 4} more
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Import YAML Configuration
            </DialogTitle>
            <DialogDescription>
              Import an existing GRC configuration from a YAML file or paste content directly
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* File Upload */}
            <div className="space-y-3">
              <Label>Upload YAML File</Label>
              <div 
                className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <FileUp className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  .yaml or .yml files only
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".yaml,.yml"
                className="hidden"
                onChange={handleFileUpload}
                data-testid="file-input"
              />
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or paste content</span>
              </div>
            </div>

            {/* Paste Content */}
            <div className="space-y-3">
              <Label>Paste YAML Content</Label>
              <Textarea
                placeholder="Paste your YAML configuration here..."
                value={pasteContent}
                onChange={(e) => setPasteContent(e.target.value)}
                rows={10}
                className="font-mono text-xs"
                data-testid="paste-textarea"
              />
              <Button 
                onClick={handlePasteImport}
                disabled={!pasteContent.trim()}
                data-testid="import-paste-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import from Paste
              </Button>
            </div>

            {/* Error Message */}
            {importError && (
              <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {importError}
                  </p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default YAMLBuilder;
